import config from 'config/config';
import { getDefaultCounter } from 'pages/PageCounter/hooks/useCounterConfig';
import { AnswersState } from 'schemas/answerStateSchema';
import { ApiItemsType } from 'store/hooks/useApiItems';
import { getPanelPower, GetPeakPower } from './calculation/calculation';
import { notifySentry } from './error/notifySentry';

/**
 * Returns the matching inverter from api items based on the answers state
 */
export const getInverterItem = (
  answers: AnswersState,
  items: ApiItemsType,
  numPanels: number
) => {
  // first retrieve the counterID
  const counterID =
    answers.counter ?? getDefaultCounter(answers.businessChoice).id;

  // it gives us the networkType
  const networkType = config.options.counter.find(
    (x) => x.id === counterID
  )?.type;

  // we need the installatino peak power to filter inverters
  const peakPower =
    GetPeakPower(
      numPanels,
      getPanelPower(
        answers.panelId,
        config.options.panels[answers.businessChoice]
      )
    ) * 1000;

  // now we filter inverters to correct conditions
  const filteredInverters = items.inverters
    // filter to have correct network type
    .filter((i) => i.networkType === networkType)
    // be sure to have the allow battery if we have a battery
    .filter((i) => !answers.needBattery || i.batteryAllowed)
    // be sure to have enough power, or we can have multiple inverters
    .filter((i) => peakPower < i.power || i.multipleInverterAllowed)
    // now we sort it so that the first element of the array is the one closed to the peak power
    .sort(
      (a, b) => Math.abs(peakPower - a.power) - Math.abs(peakPower - b.power)
    );

  // ---- Security ----
  // if no inverter is matching the network type, this is a BIG issue.
  // We notify and return the first inverter
  if (filteredInverters.length === 0) {
    notifySentry(new Error('No inverter found for the current network type'), {
      answers,
      items,
    });
  }

  // get inverterItem and num inverter needed for this configuration
  const inverterItem =
    filteredInverters.length > 0 ? filteredInverters[0] : items.inverters[0];
  const quantity = Math.ceil(peakPower / inverterItem.power);

  return { inverterItem, quantity };
};
